/**
 * Split arithmetic.
 *
 * INVARIANT (AGENTS.md §3.2): every entry has at least one split, and the
 * splits sum to exactly the entry total. This is checked here, again by a
 * database trigger, and it is why all reporting sums splits rather than the
 * entry header — the two can never disagree.
 */
import { CurrencyMismatchError, SplitMismatchError } from './errors.js';
import { allocate, allocateEvenly } from './money.js';
export function totalOfSplits(splits, currency) {
    let total = 0n;
    for (const split of splits) {
        if (split.amount.currency !== currency)
            throw new CurrencyMismatchError(currency, split.amount.currency);
        total += split.amount.amount;
    }
    return { amount: total, currency };
}
/**
 * Throws unless the splits are non-empty, all in the entry's currency, and sum
 * to the entry total exactly.
 */
export function assertSplitsBalance(entryTotal, splits) {
    if (splits.length === 0)
        throw new SplitMismatchError(entryTotal.amount, 0n);
    const actual = totalOfSplits(splits, entryTotal.currency);
    if (actual.amount !== entryTotal.amount)
        throw new SplitMismatchError(entryTotal.amount, actual.amount);
}
/**
 * Quick-add's default: one category takes the whole entry. Keeping the
 * single-category case on the same code path as the many-category case is what
 * stops "simple" entries from growing a second, subtly different, model.
 */
export function singleSplit(entryTotal, categoryId, bucketOverride) {
    return [{ categoryId, amount: entryTotal, bucketOverride: bucketOverride ?? null }];
}
/** Split an entry evenly across categories, with the remainder distributed deterministically. */
export function splitEvenly(entryTotal, categoryIds) {
    const parts = allocateEvenly(entryTotal, categoryIds.length);
    return categoryIds.map((categoryId, index) => ({
        categoryId,
        amount: parts[index],
        bucketOverride: null,
    }));
}
/** Split an entry by weights — used when the scanner knows each category's line-item subtotal. */
export function splitByWeights(entryTotal, weighted) {
    const parts = allocate(entryTotal, weighted.map((w) => w.weight));
    return weighted.map((w, index) => ({
        categoryId: w.categoryId,
        amount: parts[index],
        bucketOverride: null,
    }));
}
//# sourceMappingURL=split.js.map